import type { IARRunPrintRow, IARRunStatus } from '@/types/iar';

export interface IarWorksheetTemplateOptions {
  runNumber: string;
  runId: string;
  status: IARRunStatus;
  generatedAt: string;
  rows: IARRunPrintRow[];
  recordedQtyTotal: number;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatGeneratedAt = (generatedAt: string): string => {
  const parsed = new Date(generatedAt);
  if (Number.isNaN(parsed.getTime())) {
    return generatedAt;
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(parsed);
};

const buildRowsHtml = (rows: IARRunPrintRow[]): string => {
  return rows
    .map((row) => {
      return `<tr>
  <td>${row.sequence}</td>
  <td>${escapeHtml(row.lin)}</td>
  <td>${escapeHtml(row.nsn)}</td>
  <td>${escapeHtml(row.nomen)}</td>
  <td>${escapeHtml(row.locationName)}</td>
  <td style="text-align: right;">${row.recordedQty}</td>
  <td class="write-in"></td>
</tr>`;
    })
    .join('\n');
};

export const buildIarWorksheetReportHtml = ({
  runNumber,
  runId,
  status,
  generatedAt,
  rows,
  recordedQtyTotal,
}: IarWorksheetTemplateOptions): string => {
  const generatedLabel = formatGeneratedAt(generatedAt);
  const tableRows = buildRowsHtml(rows);

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>IAR Print • ${escapeHtml(runNumber)}</title>
    <style>
      :root {
        color-scheme: light;
      }

      body {
        margin: 24px;
        color: #111;
        font-family: "Times New Roman", Georgia, serif;
      }

      h1 {
        margin: 0 0 8px;
        font-size: 24px;
      }

      .meta {
        margin-bottom: 16px;
        font-size: 14px;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
      }

      th, td {
        border: 1px solid #222;
        padding: 6px;
        vertical-align: top;
      }

      th {
        text-align: left;
      }

      .write-in {
        width: 120px;
        min-height: 20px;
      }

      .footer {
        margin-top: 12px;
        font-size: 13px;
      }

      @media print {
        body {
          margin: 0;
        }
      }
    </style>
  </head>
  <body>
    <h1>Inventory Adjustment Report (DA444 Worksheet)</h1>
    <div class="meta">
      <div><strong>Run #:</strong> ${escapeHtml(runNumber)}</div>
      <div><strong>Internal Run ID:</strong> ${escapeHtml(runId)}</div>
      <div><strong>Status:</strong> ${escapeHtml(status)}</div>
      <div><strong>Generated:</strong> ${escapeHtml(generatedLabel)}</div>
      <div><strong>Total Lines:</strong> ${rows.length}</div>
    </div>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>LIN</th>
          <th>NSN</th>
          <th>Nomenclature</th>
          <th>Location</th>
          <th>Recorded Qty</th>
          <th>Inventoried Qty (Write-In)</th>
        </tr>
      </thead>
      <tbody>
${tableRows}
      </tbody>
    </table>
    <div class="footer">
      <div><strong>Total Recorded Qty:</strong> ${recordedQtyTotal}</div>
      <div><strong>Note:</strong> Fill inventoried quantities by hand during physical count.</div>
    </div>
  </body>
</html>`;
};
